import { Injectable } from '@angular/core';
import { NewsfeedApiService } from '@epicuro-next/data/engagement/engagement-api';
import { NewsfeedGetRequestDto } from '@epicuro-next/data/engagement/engagement-models';
import { selectCurrentSite } from '@epicuro-next/state/site-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  take,
} from 'rxjs/operators';

import * as actions from '../actions';
import { selectFetchFeedArguments } from '../news-feed.selectors';

/** News feed fetch effects */
@Injectable()
export class FetchEffects {
  /** Fetch action effect */
  public fetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.fetch),
      concatMap((action) =>
        of(action).pipe(
          switchMap(() =>
            this.store.pipe(
              select(selectCurrentSite),
              filter((site) => isNonNullish(site)),
              take(1),
            ),
          ),
        ),
      ),
      switchMap(() =>
        this.store.pipe(
          select(selectFetchFeedArguments),
          filter(isNonNullish),
          take(1),
          switchMap((params) =>
            this.feedService.fetch(params).pipe(
              map((result) => actions.fetchSuccess({ data: result })),
              catchError(() => of(actions.fetchFail())),
            ),
          ),
        ),
      ),
    ),
  );

  public fetchUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.fetchUpdates),
      concatLatestFrom(() => this.store.select(selectFetchFeedArguments)),
      filter(([_, params]) => !!params),
      switchMap(([action, params]) => {
        const { page, pageSize, ...rest } = params as NewsfeedGetRequestDto;
        return this.feedService
          .fetch({
            ...rest,
            page: 1,
            pageSize: 10,
            lastUpdate: action.date,
          })
          .pipe(
            filter((result) => Boolean(result.items.length)),
            map((data) => actions.fetchUpdatesSuccess({ data })),
            catchError(() => of(actions.fetchFail())),
          );
      }),
    ),
  );

  /** Dependencies */
  constructor(
    private actions$: Actions,
    private store: Store,
    private feedService: NewsfeedApiService,
  ) {}
}
